import { ArrowUpRight, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { formatCredits } from '@/lib/creditCosts';
import { PublicPlan } from '@/components/PlanSelector';

interface PlanBayProps {
  plan: PublicPlan;
  index: number;
  billingCycle: string;
  busy?: boolean;
  onSelect: () => void;
  onFocusChange?: (key: string | null) => void;
}

// One bay of the pricing elevation. Bays share walls; the popular plan rises above the
// roofline with its light on (see .elev / .bay in scene.css).
export default function PlanBay({ plan, index, billingCycle, busy = false, onSelect, onFocusChange }: PlanBayProps) {
  const { t } = useTranslation();
  const isCustom = plan.price == null;
  const rate = (value: number | null | undefined) =>
    value == null ? t('pricing.custom_rate') : `${formatCredits(value)} ${t('pricing.credits_unit')}`;

  return (
    <article
      className={cn('bay', plan.isPopular && 'bay--lit')}
      onMouseEnter={() => onFocusChange?.(plan.key)}
      onMouseLeave={() => onFocusChange?.(null)}
      onFocus={() => onFocusChange?.(plan.key)}
      onBlur={() => onFocusChange?.(null)}
    >
      <span className="bay__light" aria-hidden="true" />
      <div className="bay__inner">
        {plan.isPopular && <p className="bay__parapet mono">{t('pricing.most_popular')}</p>}

        <div className="grid gap-2">
          <p className="mono">{String(index + 1).padStart(2, '0')} · {billingCycle}</p>
          <h2 className="text-[1.25rem] font-medium">{plan.name}</h2>
          <p className="text-[14px] leading-[1.5] text-[var(--graphite)] min-h-[3lh]">{plan.description}</p>
        </div>

        <p className="bay__price">
          {isCustom ? (
            t('pricing.custom_rate')
          ) : (
            <>
              <span className="mono align-top me-2" style={{ fontSize: 12 }}>{plan.currency?.toUpperCase()}</span>
              {plan.price?.toLocaleString()}
            </>
          )}
        </p>

        <div className="bay__rates">
          <div>
            <span className="text-[var(--graphite)]">{t('pricing.whatsapp')} · {t('pricing.init')}</span>
            <span className="font-medium">{rate(plan.whatsappInitCredits)}</span>
          </div>
          <div>
            <span className="text-[var(--graphite)]">{t('pricing.whatsapp')} · {t('pricing.next')}</span>
            <span className="font-medium">{rate(plan.whatsappNextCredits)}</span>
          </div>
          <div>
            <span className="text-[var(--graphite)]">{t('pricing.sms')}</span>
            <span className="font-medium">{rate(plan.smsCredits)}</span>
          </div>
        </div>

        <ul className="bay__features">
          {(plan.marketingFeatures || []).map((feature, i) => <li key={i}>{feature}</li>)}
        </ul>

        <button
          type="button"
          onClick={onSelect}
          disabled={busy}
          className={cn(plan.isPopular ? 'btn-ink' : 'btn-line', 'w-full')}
        >
          {busy ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('checkout.loading', 'Redirecting…')}
            </>
          ) : (
            <>
              {isCustom ? t('pricing.contact') : t('pricing.select')}
              <ArrowUpRight className="btn-arrow h-4 w-4" />
            </>
          )}
        </button>
      </div>
    </article>
  );
}
